/**
 * Selection of the galaxy map as one small state machine (pure, unit-tested; the store only holds the result).
 *
 *   system   a star is chosen: the system panel opens, no fleet is selected
 *   fleet    one or more OWN fleets are chosen (the last one is the "primary" the panel describes); commands go to them only
 *   foreign  one foreign fleet the server showed to the player (presence rule): information only, no commands
 *   empty    nothing
 *
 * A tap on empty space clears everything; Esc first leaves the move mode, then clears the fleets.
 * In move mode a tap on a system is the destination of the command, not a selection (the caller handles that).
 */
export interface Selection {
  systemId: string | null;
  fleetIds: string[];
  /** the fleet the right-hand panel describes (always one of fleetIds), or null */
  primaryId: string | null;
  foreignFleetId: string | null;
  moveMode: boolean;
}

export const EMPTY_SELECTION: Selection = { systemId: null, fleetIds: [], primaryId: null, foreignFleetId: null, moveMode: false };

export type SelectionEvent =
  | { type: 'fleet'; fleetId: string; systemId: string; additive: boolean }
  | { type: 'fleets'; fleetIds: string[]; systemId: string | null; additive: boolean }
  | { type: 'foreign'; fleetId: string; systemId: string }
  | { type: 'system'; systemId: string }
  | { type: 'empty' }
  | { type: 'escape' }
  | { type: 'moveMode'; on: boolean }
  /** the fleets that still exist (merged / destroyed / disbanded ones drop out) */
  | { type: 'prune'; existing: ReadonlySet<string> };

function withFleets(sel: Selection, fleetIds: string[], systemId: string | null, primary?: string | null): Selection {
  const primaryId = primary !== undefined ? primary : fleetIds.includes(sel.primaryId ?? '') ? sel.primaryId : (fleetIds[fleetIds.length - 1] ?? null);
  return { ...sel, fleetIds, primaryId, systemId: fleetIds.length ? systemId : sel.systemId, foreignFleetId: null, moveMode: fleetIds.length ? sel.moveMode : false };
}

export function reduceSelection(sel: Selection, ev: SelectionEvent): Selection {
  switch (ev.type) {
    case 'fleet': {
      if (!ev.additive) return { systemId: ev.systemId, fleetIds: [ev.fleetId], primaryId: ev.fleetId, foreignFleetId: null, moveMode: false };
      const has = sel.fleetIds.includes(ev.fleetId);
      const next = has ? sel.fleetIds.filter(id => id !== ev.fleetId) : [...sel.fleetIds, ev.fleetId];
      return withFleets(sel, next, ev.systemId, has ? undefined : ev.fleetId);
    }
    case 'fleets': {
      const ids = ev.additive ? [...new Set([...sel.fleetIds, ...ev.fleetIds])] : [...new Set(ev.fleetIds)];
      if (ids.length === 0) return ev.additive ? sel : { ...EMPTY_SELECTION, systemId: sel.systemId };
      return { ...withFleets(sel, ids, ev.systemId ?? sel.systemId, ids[ids.length - 1]!), moveMode: false };
    }
    case 'foreign':
      return { systemId: ev.systemId, fleetIds: [], primaryId: null, foreignFleetId: ev.fleetId, moveMode: false };
    case 'system':
      return { systemId: ev.systemId, fleetIds: [], primaryId: null, foreignFleetId: null, moveMode: false };
    case 'empty':
      return EMPTY_SELECTION;
    case 'escape':
      if (sel.moveMode) return { ...sel, moveMode: false };
      if (sel.fleetIds.length || sel.foreignFleetId) return { ...sel, fleetIds: [], primaryId: null, foreignFleetId: null };
      return sel;
    case 'moveMode':
      return ev.on && sel.fleetIds.length === 0 ? sel : { ...sel, moveMode: ev.on };
    case 'prune': {
      const ids = sel.fleetIds.filter(id => ev.existing.has(id));
      if (ids.length === sel.fleetIds.length) return sel;
      return withFleets(sel, ids, sel.systemId);
    }
  }
}
